// weeklyReportCalc.js — 주간 경영관리 리포트(웹 화면)용 계산 유틸.
// functions/lib/calc.js · functions/lib/cashflow.js(Cloud Function, 이메일 발송용)와 동일한 로직을
// 브라우저(ES module)에서도 쓸 수 있게 옮겨뒀다. 재고 상태·발주점(ROP) 계산은 App.js가 이미
// items/reorderRows로 계산해두므로 여기서는 다시 만들지 않고, 주간 리포트에만 필요한
// 출고 급증/지연·30일 미출고·재고금액 전주대비·DIO·벤더 리드타임 실측·현금흐름만 담당한다.
// (두 파일의 로직이 어긋나지 않도록, 판정 기준을 바꿀 때는 양쪽 다 같이 고쳐야 한다.)

export const ymd = (d) => d.toISOString().slice(0, 10);
export const addDays = (dateStr, n) => { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() + n); return ymd(d); };
export const daysBetween = (a, b) => Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);

// ── 이번 주 구매계획 실적: 입고(완료) · 발주서 발행 ──
// 발주 계획 앱(purchasePlans)의 실제 필드만 사용한다 — 재고 화면의 inbound 추정치(단가×수량)가 아니라
// 발주 앱에 확정 저장된 금액(totAmt, 외화는 rate로 KRW 환산)을 그대로 집계한다.
//  · 입고(완료): done === true 이면서 date(최종 선적일)가 이번 주 구간에 속하는 발주
//  · 발주서 발행: issueDate(발행일)가 이번 주 구간에 속하는 발주 (완료 여부 무관)
//  · 지연 백로그: done !== true 이면서 date가 기준일(today)보다 과거인 발주 = 선적 예정일이 지났는데 미완료
const krwOf = (p) => {
  const amt = Number(p.totAmt) || 0;
  const usd = p.cur === "$" || String(p.cur || "").toUpperCase() === "USD";
  return usd ? amt * (Number(p.rate) || 0) : amt;
};
const planRow = (p) => ({
  id: p._id || p.id,
  vendor: p.vendor || "(거래처 미지정)",
  issueDate: p.issueDate || "",
  date: p.date || "",
  confirmedAt: p.confirmedAt || "",
  cur: p.cur || "₩",
  rate: Number(p.rate) || 0,
  totAmt: Number(p.totAmt) || 0,
  totQty: Number(p.totQty) || 0,
  krw: krwOf(p),
  payTerm: p.payTerm || "",
  done: !!p.done,
  itemCount: Array.isArray(p.rows) ? p.rows.length : 0,
});
const sum = (rows, k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);

export function computeProcurementWeekly(plans, { periodStart, periodEnd, today } = {}) {
  const list = (plans || []).map(planRow);
  const inWeek = (d) => !!d && d >= periodStart && d <= periodEnd;

  const inboundRows = list.filter((p) => p.done && inWeek(p.date)).sort((a, b) => b.krw - a.krw);
  const issuedRows = list.filter((p) => inWeek(p.issueDate)).sort((a, b) => b.krw - a.krw);
  const delayedRows = list.filter((p) => !p.done && p.date && p.date < (today || periodEnd)).sort((a, b) => (a.date < b.date ? -1 : 1));

  const agg = (rows) => ({
    rows,
    count: rows.length,
    krw: sum(rows, "krw"),
    qty: sum(rows, "totQty"),
    vendors: new Set(rows.map((r) => r.vendor)).size,
  });
  return { inbound: agg(inboundRows), issued: agg(issuedRows), delayed: agg(delayedRows) };
}

// ── 출고 급증 품목: 최근 7일 평균 ÷ 직전 4주 평균 ≥ threshold(기본 1.5) ──
export function computeSurgeItems(dailySalesByCode, items, { threshold = 1.5, today } = {}) {
  const t = today || ymd(new Date());
  const last7Start = addDays(t, -7), last7End = addDays(t, -1);
  const prior28Start = addDays(t, -35), prior28End = addDays(t, -8);
  const stockOf = new Map(items.map((it) => [it.code, it.stock]));
  const nameOf = new Map(items.map((it) => [it.code, it.name]));
  const out = [];
  Object.keys(dailySalesByCode).forEach((code) => {
    const arr = dailySalesByCode[code] || [];
    if (!arr.length) return;
    const last7 = arr.filter((x) => x.date >= last7Start && x.date <= last7End).reduce((s, x) => s + x.qty, 0) / 7;
    const prior28 = arr.filter((x) => x.date >= prior28Start && x.date <= prior28End).reduce((s, x) => s + x.qty, 0) / 28;
    if (prior28 <= 0) { if (last7 > 0) out.push({ code, name: nameOf.get(code), last7, prior28, ratio: null, note: "신규 급증(비교 데이터 없음)", stock: stockOf.get(code) ?? null }); return; }
    const ratio = last7 / prior28;
    if (ratio >= threshold) out.push({ code, name: nameOf.get(code), last7, prior28, ratio, stock: stockOf.get(code) ?? null });
  });
  return out.sort((a, b) => (b.ratio ?? 999) - (a.ratio ?? 999));
}

// ── 출고 지연 품목: 최근 90일 내 판매 이력은 있으나 최근 14일 출고 0 ──
export function computeStagnantItems(dailySalesByCode, items, { stagnantDays = 14, lookbackDays = 90, today } = {}) {
  const t = today || ymd(new Date());
  const lookStart = addDays(t, -lookbackDays);
  const recentStart = addDays(t, -stagnantDays);
  const stockOf = new Map(items.map((it) => [it.code, it.stock]));
  const nameOf = new Map(items.map((it) => [it.code, it.name]));
  const out = [];
  Object.keys(dailySalesByCode).forEach((code) => {
    const stock = stockOf.get(code);
    if (!stock || stock <= 0) return;
    const arr = dailySalesByCode[code] || [];
    const hadSaleIn90 = arr.some((x) => x.date >= lookStart && x.qty > 0);
    if (!hadSaleIn90) return;
    const recentQty = arr.filter((x) => x.date >= recentStart).reduce((s, x) => s + x.qty, 0);
    if (recentQty === 0) {
      const lastSale = arr.filter((x) => x.qty > 0).map((x) => x.date).sort().pop() || null;
      out.push({ code, name: nameOf.get(code), stock, lastSaleDate: lastSale, noSaleDays: lastSale ? daysBetween(lastSale, t) : null });
    }
  });
  return out.sort((a, b) => (b.stock || 0) - (a.stock || 0));
}

// ── 30일 이상 미출고 재고 (재고금액 큰 순) ──
export function computeNoOutboundItems(dailySalesByCode, items, { thresholdDays = 30, today } = {}) {
  const t = today || ymd(new Date());
  const out = [];
  items.forEach((it) => {
    if (!it.stock || it.stock <= 0) return;
    const arr = dailySalesByCode[it.code] || [];
    const lastSale = arr.filter((x) => x.qty > 0).map((x) => x.date).sort().pop() || null;
    const noSaleDays = lastSale ? daysBetween(lastSale, t) : null;
    const neverSold = !lastSale && arr.length > 0;
    if ((noSaleDays != null && noSaleDays >= thresholdDays) || neverSold) {
      out.push({ code: it.code, name: it.name, stock: it.stock, value: it.value, noSaleDays, neverSold });
    }
  });
  return out.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
}

// ── 재고금액 전주 대비 비교 (valueHistory에서 6~10일 전 사이 가장 가까운 스냅샷 사용) ──
export function computeValueCompare(valueHistory, currentValue, today) {
  const t = today || ymd(new Date());
  const targetMax = addDays(t, -6);
  const targetMin = addDays(t, -10);
  const dates = Object.keys(valueHistory || {}).filter((d) => d >= targetMin && d <= targetMax).sort();
  if (!dates.length) return { current: currentValue, prior: null, pct: null, priorDate: null };
  const priorDate = dates[dates.length - 1];
  const prior = valueHistory[priorDate];
  const pct = prior ? ((currentValue - prior) / Math.abs(prior)) * 100 : null;
  return { current: currentValue, prior, pct, priorDate };
}

// ── DIO(재고자산회전일수) 근사치: 재고금액 ÷ 일평균 매출원가(월판매량×단가 합/30) ──
export function computeDIO(items, totalValue) {
  let dailyCOGS = 0;
  items.forEach((it) => { if (it.hasCost && it.monthly > 0) dailyCOGS += (it.monthly * it.unitCost) / 30; });
  if (dailyCOGS <= 0) return { dio: null, dailyCOGS: 0 };
  return { dio: totalValue / dailyCOGS, dailyCOGS };
}

// ── 벤더별 리드타임 실측: 발주 계획 이력의 issueDate(발주일) → date(입고예정일) 차이 평균 ──
export function computeVendorLeadTimes(plans) {
  const map = new Map();
  (plans || []).forEach((p) => {
    if (!p.issueDate || !p.date) return;
    const lt = daysBetween(p.issueDate, p.date);
    if (!Number.isFinite(lt) || lt < 0 || lt > 365) return;
    const vendor = p.vendor || "(거래처 미지정)";
    if (!map.has(vendor)) map.set(vendor, { vendor, samples: [] });
    map.get(vendor).samples.push(lt);
  });
  const avg = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null);
  return Array.from(map.values())
    .map((v) => ({ vendor: v.vendor, count: v.samples.length, avgLT: avg(v.samples), maxLT: Math.max(...v.samples), minLT: Math.min(...v.samples) }))
    .sort((a, b) => b.count - a.count);
}

// ── 현금흐름: 매출 입금 시점 실측 반영 + 구매계획 월별 결제예상(monthlyForecast) ──
// 쿠팡: 세금계산서 발행일(=매출일) 기준 70%는 +60일, 30%는 +90일 뒤 입금.
// 그 외 모든 거래처: 세금계산서 발행일(=매출일) 기준 +3일 뒤 입금.
// (2026-07-25 확정 — 이전에는 쿠팡만 T+90/104일로 반영하고 다른 거래처 매출은 현금흐름에서 아예 빠져있었음)
function monthOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function computeInflowByMonth(salesRecords, {
  coupangVendorMatch = /쿠팡/i,
  coupangSchedule = [{ ratio: 0.7, days: 60 }, { ratio: 0.3, days: 90 }],
  otherDays = 3,
  extraDelayDays = 0,
} = {}) {
  const map = new Map();
  (salesRecords || []).forEach((r) => {
    if (coupangVendorMatch.test(r.vendorName || "")) {
      coupangSchedule.forEach(({ ratio, days }) => {
        const m = monthOf(addDays(r.date, days + extraDelayDays));
        map.set(m, (map.get(m) || 0) + r.amount * ratio);
      });
    } else {
      const m = monthOf(addDays(r.date, otherDays + extraDelayDays));
      map.set(m, (map.get(m) || 0) + r.amount);
    }
  });
  return map;
}
// ── 매입세금계산서(실제 매입) 출금 시점: 매입일이 속한 달의 말일 + 10일.
// 예외(청구 마감 주기가 있는 거래처): 전월 23일~당월 22일 매입을 "당월 매입"으로 묶어서, 그 당월의
// 말일 + 10일에 출금한다(예: 우진 — 6/23~7/22 매입은 7월 매입으로 잡혀 7월 말일+10일=8/10 출금).
// (2026-07-25 확정. 구매계획 기준 매입은 이 규칙과 무관하게 procurementPlans.js의 payTerm 로직을 그대로 쓴다.)
const PURCHASE_CYCLE_RULES = [{ match: /우진/, cycleStartDay: 23 }];

function lastDayOfMonthStr(year, month1to12) {
  let ny = year, nm = month1to12 + 1;
  if (nm > 12) { nm = 1; ny += 1; }
  return addDays(`${ny}-${String(nm).padStart(2, "0")}-01`, -1);
}

export function computeOutflowByMonthFromPurchases(purchaseRecords, { extraDelayDays = 0 } = {}) {
  const map = new Map();
  (purchaseRecords || []).forEach((r) => {
    const [yStr, mStr, dStr] = String(r.date || "").split("-");
    let y = Number(yStr), m = Number(mStr);
    const day = Number(dStr);
    if (!y || !m || !day) return;
    const rule = PURCHASE_CYCLE_RULES.find((c) => c.match.test(r.vendorName || ""));
    if (rule && day >= rule.cycleStartDay) {
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    const lastDay = lastDayOfMonthStr(y, m);
    const payDate = addDays(lastDay, 10 + extraDelayDays);
    const bucket = monthOf(payDate);
    map.set(bucket, (map.get(bucket) || 0) + r.amount);
  });
  return map;
}

// 고정비(임대료·급여·세금 등 — 구매계획/쿠팡 매출에 안 잡히는 지출) 목록을 월별 합계 맵으로 변환.
// fixedCosts: [{name, amount, recurring, month}] — recurring이면 매달, 아니면 month("YYYY-MM")에만 반영.
export function computeFixedCostByMonth(fixedCosts, months) {
  const map = new Map(months.map((m) => [m, 0]));
  (fixedCosts || []).forEach((c) => {
    const amt = Number(c.amount) || 0;
    if (!amt) return;
    if (c.recurring) months.forEach((m) => map.set(m, (map.get(m) || 0) + amt));
    else if (c.month && map.has(c.month)) map.set(c.month, (map.get(c.month) || 0) + amt);
  });
  return map;
}

// 기타 입금(정부지원 대출·보조금·투자금 등 — 매출 입금으로는 안 잡히는 현금 유입)을 월별 합계 맵으로 변환.
// otherInflows: [{name, amount, recurring, month}] — 구조·처리 방식은 고정비(computeFixedCostByMonth)와 동일하되
// 부호만 반대(유입)다. 대출처럼 1회성이면 recurring=false + month 지정, 매달 들어오는 항목이면 recurring=true.
// (2026-07-25 추가 — 7월 정부지원 대출 176,000,000원 반영 요청이 계기)
export function computeOtherInflowByMonth(otherInflows, months) {
  return computeFixedCostByMonth(otherInflows, months);
}

// monthlyForecast는 procurementPlans.js의 monthlyPaymentForecast() 결과를 그대로 받는다.
// actualBalance/fixedCosts를 넘기면 실제 통장 잔액을 기준점으로 누적 현금흐름을 계산하고,
// 구매계획에 안 잡히는 고정비도 매입 지출에 합산한다(현금흐름 관리 기능).
export function buildMonthlyCashflow({ salesRecords, monthlyForecast, purchaseInvoiceRecords = [], today, monthsAhead = 5, coupangVendorMatch = /쿠팡/i, actualBalance = 0, fixedCosts = [], otherInflows = [] }) {
  const t = today || ymd(new Date());
  const months = [];
  {
    const d = new Date(t + "T00:00:00");
    for (let i = 0; i < monthsAhead; i++) {
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
      d.setMonth(d.getMonth() + 1);
    }
  }
  // 구매계획(결제조건 payTerm) 출금 + 매입세금계산서(월말+10일, 우진 등은 청구주기 적용) 출금은
  // 시나리오(기본/보수적)와 무관하게 고정 — 보수적 시나리오는 "입금이 늦어지는" 쪽만 가정한다.
  const planOutflowMap = new Map();
  (monthlyForecast?.months || []).forEach((m) => planOutflowMap.set(m.month, m.conv));
  const invoiceOutflowMap = computeOutflowByMonthFromPurchases(purchaseInvoiceRecords);
  const fixedMap = computeFixedCostByMonth(fixedCosts, months);
  // 기타 입금(대출·보조금 등)도 시나리오와 무관하게 고정 — 보수적 시나리오는 매출 입금만 지연시킨다.
  const otherInflowMap = computeOtherInflowByMonth(otherInflows, months);

  const buildRows = (extraDelayDays) => {
    const salesInflowMap = computeInflowByMonth(salesRecords, { coupangVendorMatch, extraDelayDays });
    let cum = Number(actualBalance) || 0;
    return months.map((m) => {
      const salesInflow = salesInflowMap.get(m) || 0;
      const otherInflow = otherInflowMap.get(m) || 0;
      const inflow = salesInflow + otherInflow;
      const fixed = fixedMap.get(m) || 0;
      const planOutflow = planOutflowMap.get(m) || 0;
      const invoiceOutflow = invoiceOutflowMap.get(m) || 0;
      const outflow = planOutflow + invoiceOutflow + fixed;
      const net = inflow - outflow;
      cum += net;
      return { month: m, inflow, salesInflow, otherInflow, outflow, planOutflow, invoiceOutflow, fixed, net, cumulative: cum };
    });
  };

  return { base: buildRows(0), conservative: buildRows(14), startingBalance: Number(actualBalance) || 0 };
}
